import React, { useEffect, useRef, useState } from 'react';
import GracePeriodCountdown from './GracePeriodCountdown';
import useDeviceLocationCheck, { deviceLocationMessages } from './activities/useDeviceLocationCheck';
import './DojoComplianceBanner.css';
import useDeviceLocationPermission from './activities/useDeviceLocationPermission';
import { requiresDevicePosition } from './activities/dojoDevicePosition';

const privacyTypes = ['position', 'workout', 'meal', 'media', 'metric', 'survey'];
const names = { survey: 'Survey', position: 'Position', workout: 'Workout', meal: 'Meal', media: 'Media', metric: 'Metrics', stats: 'Stats', public: 'Public', dojo: 'Dojo', dojo_chief: 'Dojo Chief', friends: 'Friends' };
const name = value => names[value] || (typeof value === 'string' ? value.replace(/_/g, ' ') : 'Requirement');
const coachNames = { daily_insight_changes: 'Coach daily insight auto-acceptance', exercise_coach_changes: 'Coach exercise auto-acceptance' };
const violationLabel = v => {
  if (typeof v === 'string') return v;
  if (v.type === 'privacy') return `${name(v.activityType)} sharing · Required: ${name(v.required)}. Current: ${Array.isArray(v.current) && v.current.length ? v.current.map(name).join(', ') : 'Private'}.`;
  if (v.type === 'background_location_capture') return 'Background acquisition consent: explicit separate opt-in required';
  if (v.type === 'background_position_audience') return 'Background trail: exact coordinates shared with the current Dojo Chief only';
  if (v.type === 'location_capture') return 'Capture position: enable fresh device GPS when filing activities';
  if (v.type === 'coach_auto_acceptance') return `${coachNames[v.setting] || 'Coach auto-acceptance'}: required`;
  if (v.type === 'performance') return `${name(v.metric)}: ${Math.round((v.current || 0) * 100)}% (need ${Math.round(v.required * 100)}%)`;
  if (v.type === 'body_composition') return `${name(v.metric)}: ${Number.isFinite(v.current) ? v.current.toFixed(1) : 'Not recorded'} (${v.metric === 'body_fat' ? 'max' : 'min'} ${v.required})`;
  return 'Additional dojo requirement: view requirements for details';
};
const fixLabel = v => v.type === 'location_capture' ? 'Enable Capture position'
  : v.type === 'coach_auto_acceptance' && coachNames[v.setting] ? `Enable ${coachNames[v.setting]}`
  : v.type === 'privacy' && privacyTypes.includes(v.activityType) && ['public', 'dojo', 'dojo_chief', 'friends'].includes(v.required)
  ? `Enable ${name(v.activityType)} sharing with ${name(v.required)}` : null;
const positionAudiences = ['public', 'dojo', 'dojo_chief', 'friends'];
const hasPositionConsent = (profile, requirements) => {
  if (profile?.location_capture_enabled === true) return true;
  const required = requirements?.privacy?.position;
  const audiences = Array.isArray(required) ? required : [required];
  return Array.isArray(profile?.privacy_settings?.position) &&
    audiences.some(audience => positionAudiences.includes(audience) &&
      (profile.privacy_settings.position.includes(audience) || profile.privacy_settings.position.includes('public')));
};

const DojoComplianceBanner = ({ compliance, onOpen, backendService, onUpdated, onDismiss }) => {
  const needsDevice = requiresDevicePosition(compliance?.requirements) || (compliance?.violations || []).some(v => v.type === 'location_capture' || (v.type === 'privacy' && v.activityType === 'position'));
  const permission = useDeviceLocationPermission(needsDevice, compliance?.dojoId);
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const lock = useRef(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const { checking, status: deviceStatus, request: checkLocation } = useDeviceLocationCheck(backendService, compliance?.dojoId, needsDevice);
  const consentReadPending = useRef(false);
  const resumedRetryPending = useRef(false);
  const complete = compliance?.isCompliant === true || compliance?.compliant === true;
  const consentEnabled = useRef(false);
  const previousPermission = useRef('unknown');
  const latestStatus = useRef(deviceStatus);
  latestStatus.current = deviceStatus;
  useEffect(() => {
    if (!needsDevice || !complete || !backendService?.fetchUserProfile) return undefined;
    let active = true;
    let reading = false;
    let wasHidden = false;
    consentEnabled.current = false;
    previousPermission.current = 'unknown';
    const readConsent = (probeOnReturn = false) => {
      if (reading || (consentEnabled.current && !probeOnReturn)) return;
      const epoch = backendService.getLocationConsentEpoch?.();
      if (epoch === null) return; // A local write is still pending.
      reading = true;
      if (probeOnReturn) resumedRetryPending.current = true;
      consentReadPending.current = true;
      // Never join a startup GET: another consumer may have started it before
      // consent changed. Every authorization check uses its own no-store read.
      backendService.fetchUserProfile().then(profile => {
        if (!active || epoch !== backendService.getLocationConsentEpoch?.()) return;
        if (hasPositionConsent(profile, compliance?.requirements)) {
          consentEnabled.current = true;
          if (latestStatus.current == null || probeOnReturn) checkLocation();
        } else {
          consentEnabled.current = false;
        }
      }).catch(() => {}).finally(() => {
        reading = false;
        consentReadPending.current = false;
        if (active && epoch !== backendService.getLocationConsentEpoch?.() && backendService.getLocationConsentEpoch?.() !== null) readConsent();
      }); // Unknown consent never prompts.
    };
    const unsubscribe = backendService.onLocationConsentChanged?.(() => {
      consentEnabled.current = false;
      if (!reading) readConsent();
    });
    readConsent();
    const onReturn = () => {
      if (document.visibilityState === 'hidden') { wasHidden = true; resumedRetryPending.current = false; return; }
      const resume = wasHidden;
      wasHidden = false;
      // A distinct foreground resume may recover an unavailable fix. Focus
      // alone does not repeatedly prompt; disappearance of the API is a failure.
      if (latestStatus.current === 'available' && !navigator.geolocation?.getCurrentPosition) {
        checkLocation(); // Unsupported API cannot prompt or acquire coordinates.
        return;
      }
      const retry = resume && latestStatus.current && latestStatus.current !== 'available';
      readConsent(Boolean(retry));
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      active = false;
      unsubscribe?.();
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
    // A dojo's automatic probe is one-shot; rerenders must not repeat it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendService, compliance?.dojoId, needsDevice, complete]);
  useEffect(() => {
    const previous = previousPermission.current;
    previousPermission.current = permission;
    if (needsDevice && permission === 'unsupported' && deviceStatus === 'available' && !checking) {
      checkLocation(); // API absence cannot prompt; invalidate a stale success.
      return undefined;
    }
    const revoked = permission === 'denied' && deviceStatus === 'available' && previous !== 'denied';
    const restored = previous === 'denied' && permission === 'granted' && deviceStatus && deviceStatus !== 'available';
    if (restored && resumedRetryPending.current) {
      resumedRetryPending.current = false;
      return undefined; // Foreground resume already requested the fresh consented retry.
    }
    if (!needsDevice || !complete || (!revoked && !restored) || !backendService?.fetchUserProfile || consentReadPending.current) return undefined;
    const epoch = backendService.getLocationConsentEpoch?.();
    if (epoch === null) return undefined;
    let active = true;
    backendService.fetchUserProfile().then(profile => {
      if (active && epoch === backendService.getLocationConsentEpoch?.() && hasPositionConsent(profile, compliance?.requirements)) checkLocation();
    }).catch(() => {});
    return () => { active = false; };
    // Only a permission transition initiates another probe, not a render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission, needsDevice, complete, compliance?.dojoId, backendService]);

  const save = async v => {
    if (lock.current) return;
    lock.current = true; setPending(true); setError('');
    let saved = false;
    try {
      if (v.type === 'privacy') {
        const settings = await backendService.getPrivacySettings();
        if (!mounted.current) return;
        if (!settings || !privacyTypes.every(type => Array.isArray(settings[type]))) throw new Error('Could not read current sharing settings. Try again.');
        const updated = Object.fromEntries(privacyTypes.map(type => [type, settings[type]]));
        updated[v.activityType] = [...new Set([...settings[v.activityType], v.required])];
        await backendService.updatePrivacySettings(updated);
      } else if (v.type === 'location_capture') {
        // Start in the click gesture, before any network await. OS denial does
        // not revoke the separate, explicitly selected app consent.
        checkLocation();
        await backendService.updateLocationCapture(true);
      } else if (v.type === 'coach_auto_acceptance') {
        await backendService.updateCoachAutoAcceptance({ [v.setting]: true });
      }
      saved = true;
      if (!mounted.current) return;
      const profile = await backendService.fetchUserProfile();
      const rules = await backendService.getDojoRequirements(compliance.dojoId);
      if (!mounted.current) return;
      if (!profile || !rules?.requirements || !rules?.compliance) throw new Error('Could not confirm current profile and dojo requirements.');
      await onUpdated?.(profile, rules);
    } catch (e) {
      if (!mounted.current) return;
      setError(`${saved ? 'Setting saved, but refresh failed. Retry to confirm. ' : ''}${e.response?.data?.error || e.message || 'Could not save. Please try again.'}`);
    } finally { lock.current = false; if (mounted.current) setPending(false); }
  };
  if (!compliance) return null;
  const deviceFailure = needsDevice && deviceStatus && deviceStatus !== 'available';
  const deviceWarning = deviceFailure;
  const displayStatus = deviceStatus || (permission === 'denied' ? 'denied' : permission === 'unsupported' ? 'unsupported' : null);
  if (complete && !compliance.setupRetained && !compliance.refreshError && !deviceWarning) return null;
  const native = window.Capacitor?.isNativePlatform?.() === true;
  const deviceOnly = complete && deviceWarning && !(compliance.violations || []).length;
  return <section className={`dojo-compliance-banner dojo-setup-card personal-feed${deviceOnly ? ' dojo-device-only' : ''}`} aria-label={`${compliance.dojoName || 'Dojo'} requirements`}>
    <header><span className="dojo-setup-eyebrow">DOJO · {deviceWarning ? 'DEVICE ACTION NEEDED' : complete ? 'SETTINGS UPDATED' : 'ACTION NEEDED'}</span><h3>{deviceFailure ? 'Location unavailable' : complete ? 'Required settings are up to date' : `You are not meeting ${compliance.dojoName || 'dojo'} requirements`}</h3></header>
    {!deviceOnly && <p>{complete ? (needsDevice ? 'App settings are confirmed. Device location permission is separate; check it below before filing your next activity.' : 'Your current dojo requirements are confirmed.') : compliance.autoRemovalEligible === true ? <>
      Time until automatic removal if you do not comply: <GracePeriodCountdown firstViolationAt={compliance.firstViolationAt} gracePeriodDays={compliance.gracePeriodDays} expiredText="expired; removal may run at any time" />.
    </> : 'This is an advisory warning; review your requirements and privacy settings.'}</p>}
    {needsDevice && deviceWarning && <p role="alert">{compliance.dojoName || 'Your dojo'} requires device location when filing activities.</p>}
    {!!(compliance.violations || []).length && <ul className="dojo-setup-list">{compliance.violations.map((v, index) => <li key={index}>
      <div>{violationLabel(v)}</div>
      {v.type === 'privacy' && fixLabel(v) && <p>Existing sharing stays enabled.</p>}
      {v.type === 'location_capture' && <p>This saves app consent and requests device permission with a check that does not save coordinates. Device permission is separate. Fresh GPS is required when filing activities; saved gyms do not replace it.</p>}
      {v.type === 'coach_auto_acceptance' && <p>Allow these Coach changes to apply automatically without individual approval.</p>}
      {v.type === 'privacy' && v.activityType === 'position' && v.required === 'public' && <p className="dojo-setup-warning">Public Position shares your exact coordinates with anyone. Enable only if you agree.</p>}
      {fixLabel(v) && backendService && <button type="button" disabled={pending} onClick={() => save(v)}>{pending ? 'Saving…' : fixLabel(v)}</button>}
    </li>)}</ul>}
    {(compliance.requirements?.background_location_capture_enabled || compliance.requirements?.background_position_audience || (compliance.violations || []).some(v => v.type === 'background_location_capture' || v.type === 'background_position_audience')) && <div>
      <p>Background compliance is configuration-only, not tracking health. Android native wiring is not physical-device qualified; browser/iOS/old-shell capture is unsupported. OS, GPS, battery and offline failures are diagnostics, not grounds for removal. Stop and narrowing remain available.</p>
      <button type="button" className="dojo-setup-secondary" onClick={onOpen}>Review background consent →</button>
    </div>}
    {compliance.refreshError && <p role="status">Could not refresh dojo requirements. Showing the last confirmed state; try View requirements or return here to retry.</p>}
    {error && <p role="alert" className="dojo-setup-error">{error}</p>}
    {needsDevice && <div className="dojo-device-check">
      {displayStatus && <p role="status">{deviceLocationMessages[displayStatus].split('. ')[0]}.</p>}
      <button type="button" disabled={checking || pending} onClick={checkLocation}>{checking ? 'Checking device location…' : deviceStatus ? 'Retry device location' : 'Check / request device location'}</button>
      <details>
        <summary>Help</summary>
        <p>Device permission is separate from Capture position. This check requests a fresh fix but does not save or share your coordinates. Your saved sharing settings and Capture position consent stay unchanged. A device failure alone does not start a removal countdown.</p>
        {displayStatus && <p>{deviceLocationMessages[displayStatus]}</p>}
        <p>This app cannot determine whether system Location Services are on from this result.</p>
        {native ? <p>Android: Settings → Apps → Kata → Permissions → Location → Allow only while using the app; enable precise location. Also check Settings → Location.</p> : <p>Chrome on Android: Settings → Site settings → Location. On desktop: Settings → Privacy and security → Site settings → Location. Allow this site; also check your system's location permission for the browser.</p>}
        <p>iPhone / iPad: Settings → Privacy &amp; Security → Location Services. For Kata or your browser, choose While Using and enable Precise Location. Allow this site's location prompt if browsing.</p>
        <p>Return here and retry after changing settings. A failure alone does not tell us whether system location is switched off.</p>
      </details>
    </div>}
    <button type="button" className="dojo-setup-secondary" onClick={onOpen}>View requirements →</button>
    {complete && !deviceWarning && onDismiss && <button type="button" disabled={pending || checking} onClick={onDismiss}>Done</button>}
  </section>;
};
export default DojoComplianceBanner;
